import { WidgetContainer } from '../components/WidgetContainer';
import { useHasCoSellRbacRights } from 'packages/cosell/src/hooks/useHasCoSellRbacRights/useHasCoSellRbacRights';
import CoSellCallToAction from '../components/CoSellZeroStateView/CoSellZeroStateView';
import CoSellLimitedPermissionBanner from 'packages/cosell/src/pages/UnifiedInvitationDetails/AceInvitationDetails/components/CoSellLimitedPermissionBanner';
import { DisplayCloudType } from 'packages/cosell/src/types/enums';
import CollapsibleListItem from 'packages/cosell/src/components/CollapsibleListItem/CollapsibleListitem';
import useMsftReferralsForCrmId from './useMsftReferralsForCrmId';
import FormattedDateTitle from '../components/FormattedDateTitle';
import { AddCoSellButton } from '../components/AddCoSellButton';
import ReferralSummary from './ReferralSummary';
import { Loader } from '@tackle-io/platform-ui';
import useSfdcTackleWidgetPollingEnabled from 'packages/cosell/src/hooks/useSfdcTackleWidgetPollingEnabled';

export default function MicrosoftCoSells({ crmId }: { crmId: string }) {
  const rbacRights = useHasCoSellRbacRights();
  const pollingEnabled = useSfdcTackleWidgetPollingEnabled();
  const canCreateCoSell = rbacRights.createOpportunity;
  const msftReferralsQuery = useMsftReferralsForCrmId({
    crmId,
    enabled: true,
    refetchInterval: pollingEnabled ? 3000 : null,
  });
  const referrals = msftReferralsQuery.data?.referrals;

  if (msftReferralsQuery.isInitialLoading) return <Loader />;

  /** TODO: derive value from get referrals request data */
  const hasCoSells = msftReferralsQuery.data?.total > 0;

  if (!hasCoSells) {
    return (
      <CoSellCallToAction
        canCreateCoSell={canCreateCoSell}
        singleCloud={DisplayCloudType.MICROSOFT}
      />
    );
  }

  return (
    <WidgetContainer>
      <WidgetContainer.Content>
        {!rbacRights.accessAll && <CoSellLimitedPermissionBanner />}

        {referrals.map((referral, i) => (
          <CollapsibleListItem
            key={referral.tackleId}
            title={
              <FormattedDateTitle date={referral.created} prefix="Created" />
            }
            defaultOpen={i === 0}
            isCollapsible={!(i === 0 && referrals.length === 0)}
          >
            {referral.cloudProviderId ? (
              <ReferralSummary
                name={referral.title}
                status={referral.cloudStatus}
                substatus={referral.cloudSubStatus}
                tackleId={referral.tackleId}
                dealType={referral.dealType}
                dealValue={referral.dealValue}
                updatedDateTime={referral.cloudLastModified}
                targetCloseDate={referral.targetCloseDate}
                hasCloudErrors={!!referral.hasCloudErrors}
              />
            ) : (
              <IncompleteReferral {...referral} />
            )}
          </CollapsibleListItem>
        ))}
      </WidgetContainer.Content>
      {canCreateCoSell && (
        <WidgetContainer.Footer>
          <AddCoSellButton cloud={DisplayCloudType.MICROSOFT} />
        </WidgetContainer.Footer>
      )}
    </WidgetContainer>
  );
}

type ReferralSummaryProps = Pick<
  ReturnType<typeof useMsftReferralsForCrmId>['data']['referrals'][number],
  | 'tackleId'
  | 'title'
  | 'lastModified'
  | 'isMaccEligible'
  | 'targetCloseDate'
  | 'type'
>;

/** TODO: remove when actual component is added */
const IncompleteReferral = ({ tackleId, title }: ReferralSummaryProps) => {
  return (
    <div>
      <h2>Referral is not created in cloud</h2>
      <ul>
        <li>Referral name: {title}</li>
        <li>TackleId: {tackleId}</li>
      </ul>
    </div>
  );
};
